import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import UploadArea from './modal/UploadArea';
import { useFileUpload } from '../hooks/useFileUpload';
import { useToast } from '../hooks/useToast';
import { useTranslate } from '../hooks/useTranslate';
import { validateFile } from '../utils';
import DataPreviewTable from './modal/segments/DataPreviewTable';
import DataObjectConfig from './modal/DataObjectConfig';
import SchemaComparisonModal from './SchemaComparisonModal';
import { useSchemaComparison } from '../hooks/useSchemaComparison';
import { dataDescriptionService } from '../services/dataDescriptionService';
import { dataObjectService } from '../services/dataObjectService';
import { useLanguage } from '../contexts/LanguageContext';
import { TbReplaceFilled } from 'react-icons/tb';
import ContextualHelpLink from './training/ContextualHelpLink';
import type { FileAnalysis, FileColumn } from '../types';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, dataObjectData?: any) => Promise<{ jobId?: string; dataObjectName?: string } | void> | void;
  mode?: 'add' | 'analyse' | 'replace';
  selectedDataObject?: any;
  onStateChange?: (state: any) => void;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  mode = 'add',
  selectedDataObject,
  onStateChange
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const { t } = useTranslate();

  // Use the file upload hook for analysis capabilities
  const {
    fileAnalysis,
    showColumnTable,
    analyzeFileAutomatically,
    reset: resetFileUpload
  } = useFileUpload();

  // --- Schema comparison state (replace mode only) ---
  const schema = useSchemaComparison();
  const { activeLanguage } = useLanguage();
  // Replace-mode step: 'upload' → 'schema-check' → 'column-config' → 'preview'
  type ReplaceStep = 'upload' | 'schema-check' | 'column-config' | 'preview';
  const [replaceStep, setReplaceStep] = useState<ReplaceStep>('upload');
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  // Enriched analysis from Analyze API (for extra columns)
  const [enrichedAnalysis, setEnrichedAnalysis] = useState<FileAnalysis | null>(null);
  const [enriching, setEnriching] = useState(false);
  const [extraColumnNames, setExtraColumnNames] = useState<string[]>([]);
  // Config editing state (name, description, columns)
  const nameInputRef2 = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const lastUserFocusedRef = useRef<HTMLElement | null>(null);
  const [configName, setConfigName] = useState('');
  const [configDescription, setConfigDescription] = useState('');
  const [nameError, setNameError] = useState('');
  // Existing column metadata fetched from backend (descriptions, rules, etc.)
  const [existingColumnsMetadata, setExistingColumnsMetadata] = useState<FileColumn[]>([]);

  const isReplaceMode = mode === 'replace';

  // Column update handler for DataObjectConfig
  const handleColumnUpdate = useCallback((columnName: string, field: string, value: unknown) => {
    setEnrichedAnalysis(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        columns: prev.columns.map(col =>
          col.name === columnName ? { ...col, [field]: value } : col
        )
      };
    });
  }, []);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.push({
          message: validation.error || t('Invalid file type. Only CSV and Excel files are supported.'),
          variant: 'error'
        });
        // Reset file input
        event.target.value = '';
        return;
      }

      setSelectedFile(file);

      if (isReplaceMode && selectedDataObject?.uuid) {
        // Replace mode: fire schema comparison + file analysis in parallel
        setReplaceStep('schema-check');
        setAnalyzing(true);
        const schemaPromise = schema.compareWithFile(selectedDataObject.uuid, file);
        const analysisPromise = analyzeFileAutomatically(file).catch(err => {
          console.error('Failed to analyze file:', err);
        });
        const [schemaResult] = await Promise.all([schemaPromise, analysisPromise]);
        setAnalyzing(false);
        // If compatible with no extra columns, skip schema modal → go straight to preview
        const isCompatibleNoChanges = schemaResult
          && !schemaResult.is_completely_different
          && schemaResult.is_compatible
          && (!schemaResult.extra_columns || schemaResult.extra_columns.length === 0);
        if (isCompatibleNoChanges) {
          handleSchemaProceed();
        } else {
          setShowSchemaModal(true);
        }
      } else {
        // Add/Analyse mode: standard flow
        setAnalyzing(true);
        try {
          await analyzeFileAutomatically(file);
        } catch (error) {
          console.error('Failed to analyze file:', error);
          toast.push({
            message: t('Failed to analyze file. Please try again.'),
            variant: 'error'
          });
        } finally {
          setAnalyzing(false);
        }
      }
    }
  };

  const handleDrag = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      // Validate file type
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.push({
          message: validation.error || t('Invalid file type. Only CSV and Excel files are supported.'),
          variant: 'error'
        });
        return;
      }

      setSelectedFile(file);

      if (isReplaceMode && selectedDataObject?.uuid) {
        // Replace mode: fire schema comparison + file analysis in parallel
        setReplaceStep('schema-check');
        setAnalyzing(true);
        const schemaPromise = schema.compareWithFile(selectedDataObject.uuid, file);
        const analysisPromise = analyzeFileAutomatically(file).catch(err => {
          console.error('Failed to analyze file:', err);
        });
        const [schemaResult2] = await Promise.all([schemaPromise, analysisPromise]);
        setAnalyzing(false);
        // If compatible with no extra columns, skip schema modal → go straight to preview
        const isCompatibleNoChanges2 = schemaResult2
          && !schemaResult2.is_completely_different
          && schemaResult2.is_compatible
          && (!schemaResult2.extra_columns || schemaResult2.extra_columns.length === 0);
        if (isCompatibleNoChanges2) {
          handleSchemaProceed();
        } else {
          setShowSchemaModal(true);
        }
      } else {
        // Add/Analyse mode: standard flow
        setAnalyzing(true);
        try {
          await analyzeFileAutomatically(file);
        } catch (error) {
          console.error('Failed to analyze file:', error);
          toast.push({
            message: t('Failed to analyze file. Please try again.'),
            variant: 'error'
          });
        } finally {
          setAnalyzing(false);
        }
      }
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (selectedFile) {
      // Starting upload and report generation
      setReportGenerating(true);
      setAnalyzing(true);
      try {
        // Merge fileAnalysis columns (with user-modified selectedType) into the data
        // so column type changes made during preview are sent to the backend
        // In replace mode, merge existing backend metadata + user-edited new columns into the full column set
        let columnsToSend = fileAnalysis?.columns;
        if (isReplaceMode && fileAnalysis) {
          // Build a map of existing column metadata fetched from the backend
          const existingMap = new Map(existingColumnsMetadata.map(c => [c.name, c]));
          // Build a map of user-edited new columns from enriched analysis
          const editedMap = enrichedAnalysis
            ? new Map(enrichedAnalysis.columns.map(c => [c.name, c]))
            : new Map<string, FileColumn>();

          columnsToSend = fileAnalysis.columns.map(c => {
            // User-edited new columns take highest priority
            if (editedMap.has(c.name)) return editedMap.get(c.name)!;
            // Existing columns: merge backend metadata (descriptions, rules, etc.) with file-parsed info
            const existing = existingMap.get(c.name);
            if (existing) {
              return {
                ...c,
                description: existing.description || c.description,
                hasQualityIssues: existing.hasQualityIssues ?? c.hasQualityIssues,
                analysis_flag: existing.analysis_flag ?? c.analysis_flag,
                userCreatedRules: (existing as any).userCreatedRules || (c as any).userCreatedRules,
                allDqRules: (existing as any).allDqRules || (c as any).allDqRules,
              };
            }
            return c;
          });
        }
        const dataWithColumns = {
          ...selectedDataObject,
          columns: columnsToSend
        };
        const result = await onUpload(selectedFile, dataWithColumns);
        setSelectedFile(null);

        // If async analysis job started, don't close modal yet - let progress tracking handle it
        if (result && typeof result === 'object' && 'jobId' in result && result.jobId) {
          setJobId(result.jobId);
          onStateChange?.({
            jobId: result.jobId,
            dataObjectName: result.dataObjectName,
            uploadComplete: true
          });
          // Don't call onClose() - let the progress popup manage modal lifecycle
          // Keep reportGenerating=true so background processing works
        } else {
          // Regular upload without async analysis - close modal normally
          // Regular upload completed successfully
          setReportGenerating(false);
          onClose();
        }
      } catch (error) {
        console.error('[FileUploadModal] Upload failed:', error);
        setReportGenerating(false);
        onClose();
      } finally {
        setAnalyzing(false);
      }
    }
  };

  const hasWorkInProgress = () => {
    // Don't consider report generation as work that can be lost
    return (selectedFile || analyzing || showColumnTable || fileAnalysis) && !reportGenerating;
  };

  const handleClose = () => {
    // Handle close with work progress check

    // If report is generating, close immediately and continue in background
    if (reportGenerating) {
      // Report generating - closing and continuing in background
      // Continue processing in background without confirmation
      setSelectedFile(null);
      setDragActive(false);
      setReportGenerating(false);
      setJobId(null);
      resetFileUpload();
      onClose();

      // Refresh to show updated status
      window.location.reload();
      return;
    }

    // Show confirmation if there's work in progress
    if (hasWorkInProgress()) {
      // Work in progress - showing confirmation
      setShowCancelConfirmation(true);
      return;
    }

    // Proceed with normal close
    performClose();
  };

  const performClose = () => {
    setSelectedFile(null);
    setDragActive(false);
    setShowCancelConfirmation(false);
    setReportGenerating(false);
    setJobId(null);
    resetFileUpload(); // Reset file analysis state
    // Reset schema comparison state
    schema.reset();
    setReplaceStep('upload');
    setShowSchemaModal(false);
    setEnrichedAnalysis(null);
    setEnriching(false);
    setExtraColumnNames([]);
    setExistingColumnsMetadata([]);
    setConfigName('');
    setConfigDescription('');
    setNameError('');
    onClose();
  };

  const handleCancelConfirm = () => {
    setShowCancelConfirmation(false);

    // If report is generating, continue in background
    if (reportGenerating && jobId) {
      // Show background processing message
      // Reset modal state but keep the job running
      setSelectedFile(null);
      setDragActive(false);
      setReportGenerating(false);
      setJobId(null);
      resetFileUpload();
      onClose();

      // Optionally refresh the page or update parent component
      window.location.reload();
      return;
    }

    // Regular cancellation
    resetFileUpload(); // Reset file analysis state like the X button does
    onClose();
  };

  const handleCancelDeny = () => {
    setShowCancelConfirmation(false);
  };

  const resetFileSelection = () => {
    setSelectedFile(null);
    setAnalyzing(false);
    resetFileUpload();
    schema.reset();
    setReplaceStep('upload');
    setShowSchemaModal(false);
    setEnrichedAnalysis(null);
    setEnriching(false);
    setExtraColumnNames([]);
    setExistingColumnsMetadata([]);
    setConfigName('');
    setConfigDescription('');
    setNameError('');
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // --- Schema comparison handlers (replace mode) ---

  const handleSchemaGoBack = () => {
    setShowSchemaModal(false);
    resetFileSelection();
  };

  const handleSchemaProceed = async () => {
    setShowSchemaModal(false);

    // Always fetch existing column metadata from backend (like Edit Metadata flow)
    // so all metadata is available for the database update
    const fetchExistingMetadata = async (): Promise<{ columns: FileColumn[]; description: string }> => {
      try {
        const objectUuid = selectedDataObject?.uuid;
        const idToUse = objectUuid || selectedDataObject?.id;
        if (!idToUse) return { columns: [], description: '' };

        const targetLang = (activeLanguage && activeLanguage !== 'en') ? activeLanguage : undefined;
        const dataObjectWithMetadata = await dataObjectService.getDataSource(idToUse, true, targetLang);

        // Check multiple possible locations for column metadata (same pattern as Edit Metadata)
        const dataDescObj = (dataObjectWithMetadata as any).data_description;
        const extraMetadata = (dataObjectWithMetadata as any).extra_metadata;

        // Extract data object description (same pattern as DataObjectActionsModal)
        let extractedDescription = '';
        if (dataDescObj?.description) {
          extractedDescription = dataDescObj.description;
        } else if (extraMetadata?.data_description?.description) {
          extractedDescription = extraMetadata.data_description.description;
        } else if ((dataObjectWithMetadata as any).description) {
          extractedDescription = (dataObjectWithMetadata as any).description;
        }

        let columns = null;
        if (dataDescObj?.columns && Array.isArray(dataDescObj.columns)) {
          columns = dataDescObj.columns;
        } else if (extraMetadata?.data_description?.columns && Array.isArray(extraMetadata.data_description.columns)) {
          columns = extraMetadata.data_description.columns;
        } else if (extraMetadata?.columns && Array.isArray(extraMetadata.columns)) {
          columns = extraMetadata.columns;
        }

        if (!columns) return { columns: [], description: extractedDescription };

        const parsedColumns = columns.map((col: any) => {
          let sampleValues: string[] = [];
          if (col.sample_values && Array.isArray(col.sample_values)) {
            sampleValues = col.sample_values.map((v: any) => v?.toString() || '').slice(0, 5);
          } else if (col.sampleValues && Array.isArray(col.sampleValues)) {
            sampleValues = col.sampleValues.map((v: any) => v?.toString() || '').slice(0, 5);
          }

          return {
            name: col.name,
            detectedType: col.type || col.logical_type || 'String',
            selectedType: col.type || col.logical_type || 'String',
            description: col.description || '',
            sampleValues,
            hasQualityIssues: false,
            analysis_flag: col.analysis_flag ?? true,
            userCreatedRules: col.user_created_rules || [],
            allDqRules: col.all_dq_rules || []
          } as FileColumn;
        });

        return { columns: parsedColumns, description: extractedDescription };
      } catch (err) {
        console.error('Failed to fetch existing column metadata:', err);
        return { columns: [], description: '' };
      }
    };

    if (schema.hasExtraColumns && selectedFile) {
      // Extra columns detected → fetch existing metadata + analyze new columns in parallel
      setReplaceStep('column-config');
      setEnriching(true);
      try {
        const [descResult, existingMetadata] = await Promise.all([
          dataDescriptionService.analyzeFile(
            selectedFile,
            activeLanguage || undefined,
            schema.extraColumnNames
          ),
          fetchExistingMetadata()
        ]);

        // Store existing column metadata for use in handleUpload
        setExistingColumnsMetadata(existingMetadata.columns);

        // Build enriched FileAnalysis: merge file analysis columns with description API columns
        const baseColumns: FileColumn[] = fileAnalysis?.columns ?? [];
        const extras = new Set(schema.extraColumnNames);
        setExtraColumnNames(schema.extraColumnNames);

        // Enrich base columns with description data from Analyze API
        const enrichedColumns = baseColumns.map(col => {
          const descCol = descResult.columns.find(dc => dc.name === col.name);
          if (descCol) {
            return {
              ...col,
              description: descCol.description,
              hasQualityIssues: descCol.hasQualityIssues,
              detectedType: (descCol.dataType as any) || col.detectedType,
              sampleValues: descCol.sampleValues?.length ? descCol.sampleValues : col.sampleValues,
            };
          }
          return col;
        });

        // Only include new/extra columns for the metadata editing step
        const newColumnsOnly = enrichedColumns.filter(c => extras.has(c.name));

        setEnrichedAnalysis({
          fileName: fileAnalysis?.fileName ?? selectedFile.name,
          fileSize: fileAnalysis?.fileSize ?? selectedFile.size,
          columns: newColumnsOnly,
          recordCount: fileAnalysis?.recordCount ?? 0,
          totalRows: fileAnalysis?.totalRows,
          previewData: fileAnalysis?.previewData,
          source: fileAnalysis?.source,
        });
        setConfigName(selectedDataObject?.name ?? '');
        // Use existing backend description first, then Analyze API description as fallback
        setConfigDescription(existingMetadata.description || descResult.description || selectedDataObject?.description || '');
      } catch (err) {
        console.error('Failed to enrich extra columns:', err);
        toast.push({
          message: t('Failed to analyze new columns. Proceeding with preview.'),
          variant: 'warning'
        });
        // Fallback: skip column-config, go straight to preview
        setReplaceStep('preview');
      } finally {
        setEnriching(false);
      }
    } else {
      // No extra columns → still fetch existing metadata, then skip to preview
      setEnriching(true);
      try {
        const existingMetadata = await fetchExistingMetadata();
        setExistingColumnsMetadata(existingMetadata.columns);
        setConfigDescription(existingMetadata.description || selectedDataObject?.description || '');
      } catch {
        // Non-critical — proceed without existing metadata
      } finally {
        setEnriching(false);
      }
      setReplaceStep('preview');
    }
  };

  const handleColumnConfigUpdate = async () => {
    // User clicked "Update & Continue" on column config → save metadata to backend, then move to preview
    if (!selectedDataObject?.id) {
      setReplaceStep('preview');
      return;
    }

    setEnriching(true);
    try {
      // Build the full merged column set: existing backend metadata + user-edited new columns
      const existingMap = new Map(existingColumnsMetadata.map(c => [c.name, c]));
      const editedMap = enrichedAnalysis
        ? new Map(enrichedAnalysis.columns.map(c => [c.name, c]))
        : new Map<string, FileColumn>();

      const allColumns: FileColumn[] = (fileAnalysis?.columns ?? []).map(c => {
        if (editedMap.has(c.name)) return editedMap.get(c.name)!;
        const existing = existingMap.get(c.name);
        if (existing) {
          return {
            ...c,
            description: existing.description || c.description,
            hasQualityIssues: existing.hasQualityIssues ?? c.hasQualityIssues,
            analysis_flag: existing.analysis_flag ?? c.analysis_flag,
          };
        }
        return c;
      });

      // Clean columns — remove local-only rule fields (same pattern as Edit Metadata)
      const cleanColumns = allColumns.map(col => {
        const cleanCol = { ...col };
        delete cleanCol.rules;
        return cleanCol;
      });

      await dataObjectService.updateDataSource(selectedDataObject.id, {
        dataObjectUuid: selectedDataObject.uuid,
        description: configDescription.trim(),
        columns: cleanColumns,
        targetLanguage: activeLanguage || undefined,
      });

      toast.push({ message: t('Metadata updated successfully'), variant: 'success' });
    } catch (err) {
      console.error('Failed to update metadata:', err);
      toast.push({ message: t('Failed to update metadata. Continuing to preview.'), variant: 'warning' });
    } finally {
      setEnriching(false);
    }

    setReplaceStep('preview');
  };

  const handleSchemaSkipToPreview = () => {
    // Skip column enrichment, go directly to preview
    setShowSchemaModal(false);
    setReplaceStep('preview');
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalTitle = mode === 'analyse' ? t('Analyse File') : t('Upload File');

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-auto" onClick={() => { }}>
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-gray-900/50 to-black/70 backdrop-blur-md" />
      <div
        className="relative rounded-3xl border border-gray-200/50 shadow-2xl overflow-hidden flex flex-col"
        style={{
          width: 'clamp(480px, 65vw, 960px)',
          height: 'clamp(400px, 70vh, 780px)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Header - matching DataObjectActionsModal */}
        <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-8 py-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <TbReplaceFilled className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white tracking-tight">{modalTitle}</h2>
                {mode === 'replace' && selectedDataObject && (
                  <p className="text-red-100 text-sm font-medium">{t('Replacing:')} <span className="text-white font-semibold">{selectedDataObject.name}</span></p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ContextualHelpLink videoId="tv-002" variant="icon-light" />
              <button
                onClick={handleClose}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-white transition-all duration-200 hover:scale-105"
                aria-label="Close dialog"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Upload area - dotted lines contained */}
          {!selectedFile && (
            <div className="file-replace-upload-container flex-1 flex flex-col min-h-0 overflow-hidden" style={{ padding: 'clamp(0.75rem, 2vh, 1.5rem) clamp(1rem, 2vw, 2rem)' }}>
              <UploadArea
                dragActive={dragActive}
                analyzing={analyzing}
                onDrag={handleDrag}
                onDrop={handleDrop}
                onFileSelect={handleFileSelect}
                openFileDialog={openFileDialog}
                fileInputRef={fileInputRef}
              />
            </div>
          )}

          {/* Selected file info */}
          {selectedFile && (
            <div className="flex flex-col flex-1 min-h-0 overflow-y-auto" style={{ padding: 'clamp(0.75rem, 2vh, 1.5rem) clamp(1rem, 2vw, 2rem)' }}>

              {/* Replace mode: schema check loading */}
              {isReplaceMode && replaceStep === 'schema-check' && analyzing && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="loader-red loader-md"></div>
                    <span className="text-sm text-gray-500 font-medium">{t('Validating schema compatibility...')}</span>
                  </div>
                </div>
              )}

              {/* Replace mode: column enrichment loading */}
              {isReplaceMode && replaceStep === 'column-config' && enriching && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="loader-red loader-md"></div>
                    <span className="text-sm text-gray-500 font-medium">{t('Analyzing new columns...')}</span>
                  </div>
                </div>
              )}

              {/* Replace mode: column configuration editor */}
              {isReplaceMode && replaceStep === 'column-config' && !enriching && enrichedAnalysis && (
                <div className="flex flex-col flex-1 min-h-0 mt-3 overflow-y-auto">
                  <DataObjectConfig
                    dataObjectName={configName}
                    dataObjectDescription={configDescription}
                    nameError={nameError}
                    onNameChange={setConfigName}
                    onDescriptionChange={setConfigDescription}
                    nameInputRef={nameInputRef2 as React.RefObject<HTMLInputElement>}
                    descriptionRef={descriptionRef as React.RefObject<HTMLTextAreaElement>}
                    lastUserFocusedRef={lastUserFocusedRef as React.MutableRefObject<HTMLElement | null>}
                    analysis={enrichedAnalysis}
                    onColumnUpdate={handleColumnUpdate}
                    isValid={true}
                    isProcessing={false}
                    onSubmit={handleColumnConfigUpdate}
                    submitLabel={t('Update & Continue')}
                    showFooter={false}
                    newColumnNames={extraColumnNames}
                    hideHeader={true}
                    dataObjectId={selectedDataObject?.uuid}
                  />
                </div>
              )}

              {/* Replace mode: preview (after schema OK or column config done) */}
              {isReplaceMode && replaceStep === 'preview' && !analyzing && (
                <div className="flex flex-col flex-1 min-h-0 mt-3">
                  <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden min-h-0 shadow-sm">
                    <DataPreviewTable analysis={fileAnalysis!} />
                  </div>
                </div>
              )}

              {/* Add/Analyse mode: standard loading */}
              {!isReplaceMode && analyzing && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="loader-red loader-md"></div>
                    <span className="text-sm text-gray-500 font-medium">{t('Analyzing file content...')}</span>
                  </div>
                </div>
              )}

              {/* Add/Analyse mode: standard preview */}
              {!isReplaceMode && !analyzing && fileAnalysis && showColumnTable && (
                <div className="flex flex-col flex-1 min-h-0 mt-3">
                  <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden min-h-0 shadow-sm">
                    <DataPreviewTable analysis={fileAnalysis} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-4 bg-gradient-to-r from-red-50 to-pink-50 border-t border-red-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            {selectedFile && (
              <button
                onClick={resetFileSelection}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-red-700 bg-white border border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('Choose Different File')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleClose} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-800 transition-all duration-200">
              {t('Cancel')}
            </button>

            {/* Replace mode: column-config step → "Update & Continue" button */}
            {isReplaceMode && replaceStep === 'column-config' && !enriching && enrichedAnalysis && (
              <button
                onClick={handleColumnConfigUpdate}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-green-600 via-green-700 to-green-800 rounded-xl shadow-lg shadow-green-500/25 hover:shadow-green-500/40 hover:scale-105 transition-all duration-200"
              >
                {t('Update & Continue')}
              </button>
            )}

            {/* Replace mode: preview step → "Upload File" button */}
            {isReplaceMode && replaceStep === 'preview' && (
              <button
                onClick={handleUpload}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-red-600 via-red-700 to-red-800 rounded-xl shadow-lg shadow-red-500/25 hover:shadow-red-500/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                disabled={!selectedFile || analyzing}
              >
                {t('Upload File')}
              </button>
            )}

            {/* Add/Analyse mode: standard upload button */}
            {!isReplaceMode && (
              <button
                onClick={handleUpload}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-red-600 via-red-700 to-red-800 rounded-xl shadow-lg shadow-red-500/25 hover:shadow-red-500/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                disabled={!selectedFile || analyzing}
              >
                {analyzing ? (
                  <>
                    <div className="loader-red loader-sm" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                    {t('Analyzing...')}
                  </>
                ) : (
                  mode === 'add' ? t('Upload') : t('Analyse')
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-200/50"
            style={{ boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)' }}>
            <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-white">
                  {reportGenerating ? t('Report Generation in Progress') : t('Confirm Cancellation')}
                </h3>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 text-sm leading-relaxed">
                {reportGenerating
                  ? t('A report is being generated. If you close now, you can monitor its progress from the main screen.')
                  : t('If you cancel now, any progress will be lost and you will need to start over.')
                }
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={handleCancelDeny}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-800 transition-all duration-200"
              >
                {reportGenerating ? t('Stay & Monitor') : t('Continue Working')}
              </button>
              <button
                onClick={handleCancelConfirm}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-red-600 via-red-700 to-red-800 rounded-xl shadow-lg shadow-red-500/25 hover:shadow-red-500/40 hover:scale-105 transition-all duration-200"
              >
                {reportGenerating ? t('Process in Background') : t('Yes, Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schema Comparison Modal (replace mode) */}
      <SchemaComparisonModal
        isOpen={showSchemaModal}
        onClose={handleSchemaGoBack}
        onProceed={handleSchemaProceed}
        onSkipToPreview={handleSchemaSkipToPreview}
        result={schema.result}
        loading={schema.loading}
        error={schema.error}
        sourceType="file"
        dataObjectName={selectedDataObject?.name ?? ''}
        onRetry={selectedFile && selectedDataObject?.uuid
          ? () => schema.compareWithFile(selectedDataObject!.uuid, selectedFile!)
          : undefined
        }
      />
    </div>,
    document.body
  );
};

export default FileUploadModal;